import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "add-stylesheet"
    }}>{`Add stylesheet`}</h1>
    <p>{`Styling your Page Builder components is the same as styling any other component in PWA Studio. However, the CSS stylesheet for your component should define CSS classes that are comparable to the CSS classes you use on the frontend for original content type (`}<inlineCode parentName="p">{`view/frontend/web/css/source/content-type/<your-content-type>/_import.less`}</inlineCode>{`). The following steps highlight the tasks for setting up the CSS classes for your component:`}</p>
    <p><img parentName="p" {...{
        "src": "/commerce/pwa-studio/assets/ea8098fd94ec5a59ad3d2e358839d601/AddStylesheetSteps.svg",
        "alt": "Add Stylesheet steps"
      }}></img></p>
    <h2 {...{
      "id": "step-1-add-content-type-classes"
    }}>{`Step 1: Add content type classes`}</h2>
    <p>{`Start by adding the CSS classes from the frontend css of your content type. For our Quote content type, we copied the frontend styles (`}<inlineCode parentName="p">{`app/code/Example/PageBuilderQuote/view/frontend/web/css/source/content-type/example-quote/_import.less`}</inlineCode>{`) and added them to our `}<inlineCode parentName="p">{`exampleQuote.css`}</inlineCode>{`.`}</p>
    <h2 {...{
      "id": "step-2-convert-less-to-css"
    }}>{`Step 2: Convert LESS to CSS`}</h2>
    <p>{`PWA studio uses `}<a parentName="p" {...{
        "href": "/commerce/pwa-studio/guides/general-concepts/css-modules/"
      }}>{`CSS Modules`}</a>{`. It does not include tooling for LESS pre-processors, so you will need to convert your LESS into standard CSS style rules. For example, the LESS stylesheet for our Quote content type is shown here, followed by the equivalent CSS we included in our `}<inlineCode parentName="p">{`exampleQuote.css`}</inlineCode>{`.`}</p>
    <p><strong parentName="p">{`Quote content type LESS styles`}</strong>{` (`}<inlineCode parentName="p">{`_import.less`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-scss"
      }}>{`& when (@media-common = true) {
  blockquote.quote {
    display: block;
    font-size: 1.3em;
    margin: 1em;
    padding: 0.5em 10px;
    quotes: "\\201C""\\201D""\\2018""\\2019";
    text-decoration: none;
    word-break: normal !important;
    line-height: 1.5;
    padding: 0;
    font-weight: 300;
    &:before {
      content: open-quote;
      font-size: 2.6em;
      margin-right: 0.2em;
      vertical-align: -0.35em;
      line-height: 0;
      margin-left: -0.5em;
      font-weight: 300;
    }
    &:after {
      content: close-quote;
      font-size: 0;
      line-height: 0;
      margin-left: 0;
    }
  }
  div {
    &.quote-author {
      text-align: left;
      padding-right: 30px;
      font-size: 14px;
      color: #666;
      margin-left: 1.1em;
    }
    &.quote-description {
      text-align: left;
      padding-right: 30px;
      font-size: 14px;
      color: #999;
      margin-left: 1.1em;
      font-weight: 300;
    }
  }
  .black-quote {
    color: #333333;
    &:before {
      color: #333333;
    }
  }
  .blue-quote {
    color: #007ab9;
    &:before {
      color: #71adcc;
    }
  }
  .green-quote {
    color: #009900;
    &:before {
      color: #009900;
    }
  }
  .red-quote {
    color: #990000;
    &:before {
      color: #990000;
    }
  }
  .purple-quote {
    color: #990099;
    &:before {
      color: #990099;
    }
  }
}
`}</code></pre>
    <p><strong parentName="p">{`Equivalent component styles`}</strong>{` (`}<inlineCode parentName="p">{`exampleQuote.css`}</inlineCode>{`):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`.quote {
  display: block;
  font-size: 1.4em;
  margin: 1em 1em 0.3em 0.6em;
  quotes: "\\201C""\\201D""\\2018""\\2019";
  text-decoration: none;
  font-style: italic;
  word-break: normal !important;
  padding: 0;
  font-weight: 300;
}
.quote:before {
  content: open-quote;
  font-size: 2.4em;
  margin-right: 0.1em;
  vertical-align: -0.3em;
  line-height: 0;
  margin-left: -0.3em;
  font-weight: 300;
}
.quote:after {
  content: close-quote;
  font-size: 0;
  line-height: 0;
  margin-left: 0;
}
.quoteAuthor {
  padding-right: 30px;
  font-size: 16px;
  color: #333333;
  line-height: 24px;
  margin-left: 1.5em;
  word-break: normal !important;
  font-weight: 600;
}
.quoteDescription {
  position: relative;
  padding-right: 30px;
  font-size: 16px;
  line-height: 18px;
  color: #666666;
  margin-left: 1.5em;
  font-weight: 300;
  word-break: normal !important;
}
.blackQuote {
  color: #333333;
}
.blackQuote::before {
  color: #333333;
}
.blueQuote {
  color: #007ab9;
}
.blueQuote::before {
  color: #007ab9;
}
.greenQuote {
  color: #009900;
}
.greenQuote::before {
  color: #009900;
}
.redQuote {
  color: #990000;
}
.redQuote::before {
  color: #990000;
}
.purpleQuote {
  color: #990099;
}
.purpleQuote::before {
  color: #990099;
}
`}</code></pre>
    <h2 {...{
      "id": "step-3-rename-classes-optional"
    }}>{`Step 3: Rename classes (optional)`}</h2>
    <p>{`Renaming your class names to use camelCase instead of hyphens allows you to work with CSS classes in your component as if they were JavaScript object properties. For example, if you name the class for the quote author's name as `}<inlineCode parentName="p">{`.quote-author`}</inlineCode>{` (with a hyphen), you must access this class in your component like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html"
      }}>{`<div className={classes['quote-author']}>{author}</div>
`}</code></pre>
    <p>{`This not only uses a string which is prone to errors, but you don't have the benefit of your editor's intellisense.`}</p>
    <p>{`Compare that to using camelCase for your hyphenated class names by renaming `}<inlineCode parentName="p">{`.quote-author`}</inlineCode>{` to `}<inlineCode parentName="p">{`.quoteAuthor`}</inlineCode>{` and thereby enabling access to CSS classes using dot syntax, as shown here:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "740px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "30.624999999999996%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/5530d/AddStylesheetCamelCase.webp 320w", "/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/0c8fb/AddStylesheetCamelCase.webp 640w", "/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/c30c1/AddStylesheetCamelCase.webp 740w"],
            "sizes": "(max-width: 740px) 100vw, 740px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/dd4a7/AddStylesheetCamelCase.png 320w", "/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/0f09e/AddStylesheetCamelCase.png 640w", "/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/b38af/AddStylesheetCamelCase.png 740w"],
            "sizes": "(max-width: 740px) 100vw, 740px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/commerce/pwa-studio/static/f4a1cee17dfa3b9fd7531428281b9981/b38af/AddStylesheetCamelCase.png",
            "alt": "Change class names to camelCase",
            "title": "Change class names to camelCase",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p><strong parentName="p">{`Protip`}</strong>{`: Use camelCase to rename your CSS classes so that you can use dot syntax and intellisense to browse through your style rules more easily when applying them to your component.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      